import React from 'react'

import { useSearchParams } from 'next/navigation'

import { SplitsLink } from '~/components/Link'
import { type CLHref } from '~/components/component-library/types'
import { getNavigationUri } from '~/utils/devices'

type ButtonLinkProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  'href' | 'onClick'
> & {
  ref?: React.RefObject<HTMLAnchorElement | null>
  href: CLHref
  /** Disabled or loading: the link is aria-disabled, unfocusable, and never opens a window */
  isInert: boolean
  onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void
}

export const ButtonLink = ({
  ref,
  href,
  isInert,
  tabIndex,
  onClick,
  children,
  ...restProps
}: ButtonLinkProps) => {
  const searchParams = useSearchParams()
  // Only internal paths carry the chainId; external URLs pass through untouched
  const path = href.path.startsWith('/')
    ? getNavigationUri(searchParams, href.path)
    : href.path

  return (
    <SplitsLink
      {...restProps}
      path={path}
      // SplitsLink opens 'window' targets unconditionally, so an inert link drops its target and onClick cancels the navigation
      target={isInert ? undefined : href.target}
      ref={ref}
      tabIndex={isInert ? -1 : tabIndex}
      aria-disabled={isInert || undefined}
      onClick={onClick}
    >
      {children}
    </SplitsLink>
  )
}
